import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, Buton, Kart, MetinAlani } from "@koclukpanel/ui";
import { ApiHatasi, oyunKocLimiti, oyunOgrenciOzeti, oyunPuanVer } from "../api";

/**
 * PUAN KARTI — F-GAM-10/15 (oyunlaştırma dalga 1). Öğrenci 360° genel bakışta.
 *
 * Koç atanmış öğrencisinin bakiyesini görür (Emre K5, 06.10.2026) ve gerekçeyle puan verebilir. Limitler SUNUCUDA:
 * hareket başına ve öğrenci-hafta başına (bütün koçların toplamı) tavan kural sürümünden; burada yalnız gösterilir.
 * Hareket dökümü bu dalgada koç ekranında YOK (sonraki dalga).
 *
 * İstek kimliği form açılınca bir kez üretilir: ağ hatasında yeniden gönderim aynı hareketi döndürür, iki kez puan
 * yazılmaz (`MANUEL:{kimlik}` tekilliği).
 */
export function PuanKarti({ ogrenciId, verebilir }: { ogrenciId: string; verebilir: boolean }) {
  const [form, setForm] = useState(false);
  const ozet = useQuery({
    queryKey: ["oyun-ozet", ogrenciId],
    queryFn: () => oyunOgrenciOzeti(ogrenciId),
    retry: false,
  });

  // Modül kapalıysa (404) kart HİÇ çizilmiyor — "kapalı" yazan bir kart koçun işine yaramaz.
  if (ozet.error instanceof ApiHatasi && ozet.error.durum === 404) return null;

  const bekleyen = ozet.data?.bekleyen.reduce((t, b) => t + b.toplam, 0) ?? 0;
  return (
    <Kart baslik="Puan">
      {ozet.isPending ? (
        <p className="text-notr-600 text-sm">Yükleniyor…</p>
      ) : ozet.error ? (
        <p role="alert" className="text-hata-700 text-sm">
          Puan bilgisi alınamadı.
        </p>
      ) : (
        <div className="flex flex-col gap-1 text-sm">
          <p className="text-notr-900 text-lg font-semibold tabular-nums">{ozet.data.bakiye}</p>
          <p className="text-notr-600">
            Bu hafta bekleyen <span className="tabular-nums">{bekleyen}</span> (Pazartesi
            kesinleşir)
          </p>
          {verebilir &&
            (form ? (
              <PuanVerFormu ogrenciId={ogrenciId} onKapat={() => setForm(false)} />
            ) : (
              <div>
                <Buton gorunum="ikincil" onClick={() => setForm(true)}>
                  Puan ver
                </Buton>
              </div>
            ))}
        </div>
      )}
    </Kart>
  );
}

function PuanVerFormu({ ogrenciId, onKapat }: { ogrenciId: string; onKapat: () => void }) {
  const qc = useQueryClient();
  const [istekKimligi] = useState(() => crypto.randomUUID());
  const [miktar, setMiktar] = useState("");
  const [gerekce, setGerekce] = useState("");
  const limit = useQuery({
    queryKey: ["oyun-koc-limiti", ogrenciId],
    queryFn: () => oyunKocLimiti(ogrenciId),
    retry: false,
  });
  const mut = useMutation({
    mutationFn: () =>
      oyunPuanVer(ogrenciId, { miktar: Number(miktar), gerekce: gerekce.trim(), istekKimligi }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["oyun-ozet", ogrenciId] });
      void qc.invalidateQueries({ queryKey: ["oyun-koc-limiti", ogrenciId] });
      onKapat();
    },
  });
  const n = Number(miktar);
  const ust = limit.data ? Math.min(limit.data.hareketTavani, limit.data.kalan) : null;
  const gecerli =
    Number.isInteger(n) && n >= 1 && (ust === null || n <= ust) && gerekce.trim().length >= 3;
  return (
    <form
      className="mt-2 flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (gecerli) mut.mutate();
      }}
    >
      {limit.data && (
        <p className="text-notr-600 text-xs">
          Bu hafta bu öğrenciye en çok {limit.data.kalan} puan daha verilebilir (bütün koçların
          toplamı {limit.data.haftalikTavan}; bir seferde en çok {limit.data.hareketTavani}).
        </p>
      )}
      <Alan
        etiket="Puan"
        type="number"
        inputMode="numeric"
        min={1}
        max={ust ?? undefined}
        value={miktar}
        onChange={(e) => setMiktar(e.target.value)}
        zorunlu
      />
      <MetinAlani
        etiket="Gerekçe"
        value={gerekce}
        onChange={(e) => setGerekce(e.target.value)}
        rows={2}
        maxLength={1000}
        required
      />
      <p className="text-notr-600 text-xs">Gerekçe öğrencinin hareket listesinde görünür.</p>
      {mut.error && (
        <p role="alert" className="text-hata-700 text-xs">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Puan verilemedi."}
        </p>
      )}
      <div className="flex gap-2">
        <Buton type="submit" disabled={!gecerli || mut.isPending}>
          Ver
        </Buton>
        <Buton type="button" gorunum="ikincil" onClick={onKapat}>
          Vazgeç
        </Buton>
      </div>
    </form>
  );
}
